import {gql} from '@apollo/client';
import {useCallback, useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {createAgentFactory as createAgentFactoryApi} from 'llm/chat-library/factories/AgentFactoriesDAO';

import {
  AgentFactoriesQuery as AgentFactoriesQueryData,
  AgentFactoriesQuery_agentFactories_AgentFactoryNode as AgentFactorySummary,
} from './__generated/AgentFactoriesQuery';

export type {AgentFactorySummary};

const AGENT_FACTORIES_QUERY = gql`
  query AgentFactoriesQuery @engComponent(name: LLM) @skipAudit {
    agentFactories {
      id
      name
      configuration
      createdAt
      creatorName
    }
  }
`;

interface UseAgentFactoriesResult {
  agentFactories: Array<AgentFactorySummary>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  createAgentFactory: (name: string, description: string) => Promise<void>;
}

export default function useAgentFactories(): UseAgentFactoriesResult {
  const queryResult = useBenchlingQuery<AgentFactoriesQueryData>(AGENT_FACTORIES_QUERY, {
    fetchPolicy: 'network-only',
  });

  const agentFactories: Array<AgentFactorySummary> = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return queryResult.data.agentFactories;
  }, [queryResult]);

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const isError = queryResult.type === QueryResultType.ERROR;

  const createAgentFactory = useCallback(
    async (name: string, description: string) => {
      await createAgentFactoryApi(name, description);
      await queryResult.refetch();
    },
    [queryResult]
  );

  return {agentFactories, isLoading, isError, refetch: queryResult.refetch, createAgentFactory};
}
